/**
 * The fifteen section ids and how tall each one is (POS2-1136 CP3).
 *
 * ESG's four are deferred entirely, including any schema hook. `itemsW` appears in both the Weekly
 * and the Monthly and `break` in both the Monthly and the deferred ESG, which is why an enabled set
 * is only meaningful alongside its report type — the union is shared, the membership is not.
 *
 * **Heights are what the packer reserves, and they are calibrated against a real render.** The page
 * count is `paginate().length` and the document renders one page per group, so the count and the
 * document can never disagree — that is what makes the renderer's assertion self-consistency rather
 * than agreement with a constant somebody typed in a spec.
 *
 * What the heights decide is *where the breaks fall*, and getting them wrong is visible: the first
 * version of this file guessed them 1.5–4x too generous, so a Weekly whose content came to 542 px of
 * a 883 px page broke onto a second page holding one 70 px callout. The numbers below are measured
 * from a live render plus headroom for the ways each section grows, and the ones that grow with the
 * data are computed from the payload in `layout.ts` rather than fixed here.
 *
 * A section is not boxed to its height — the wrapper carries no `height` — so an underestimate does
 * not clip the section, it eats into the page's own overflow. The e2e render gate asserts no section
 * exceeds what was reserved for it, which is the check that keeps these honest.
 */

import type { CapturePhotoData } from "./types";

export const SECTION_IDS = [
  "moneyD",
  "itemsD",
  "photo",
  "actD",
  "moneyW",
  "trendW",
  "itemsW",
  "working",
  "actW",
  "moneyM",
  "trendM",
  "break",
  "winsHero",
  "streaks",
  "pinup",
] as const;

export type SectionId = (typeof SECTION_IDS)[number];

/** Section order per report — the same order `SECTIONS_BY_REPORT` declares on the backend. */
export const SECTIONS_BY_REPORT: Record<string, readonly SectionId[]> = {
  daily: ["moneyD", "itemsD", "photo", "actD"],
  weekly: ["moneyW", "trendW", "itemsW", "working", "actW"],
  monthly: ["moneyM", "trendM", "break", "itemsW", "working"],
  wins: ["winsHero", "streaks", "pinup"],
};

/**
 * Base height in CSS pixels at A4/96dpi — what a section occupies before its content is counted.
 *
 * Measured against a live render, then given headroom for the ways each section grows: a Hero
 * renders 71 and a Wins hero 95, a Callout 70 with three wrapped lines reaching ~110, PeriodBars 170
 * against its fixed 150 px chart, SplitBars 147 with its three parts. The list sections move with
 * the data, so theirs is the chrome only and `layout.ts` adds the rows.
 *
 * The first pass at these numbers put the item chrome at 24 and a row at 42, and the render gate
 * caught it: three rows measure 161 where the model said 150. A row is ~46 and the chrome ~30. The
 * gate is why that is a corrected number rather than a page break in the wrong place.
 */
export const SECTION_BASE_HEIGHT: Record<SectionId, number> = {
  moneyD: 120,
  itemsD: 30,
  photo: 0,
  actD: 115,
  moneyW: 120,
  trendW: 180,
  itemsW: 30,
  working: 115,
  actW: 115,
  moneyM: 120,
  trendM: 180,
  break: 160,
  winsHero: 120,
  streaks: 24,
  pinup: 115,
};

/** One item row, and the "everything else" line under them. Measured, not guessed. */
export const ITEM_ROW_HEIGHT = 46;
export const ITEM_REMAINDER_HEIGHT = 30;
/** Most item sections show five at most (`TOP_ITEM_ROWS` in the registry). */
export const MAX_ITEM_ROWS = 5;

/** One streak row. With no wins the section falls back to a Callout instead. */
export const STREAK_ROW_HEIGHT = 48;

/** The capture frame: a 190 px image plus its eyebrow. Zero when there is no photo to draw. */
export const CAPTURE_HEIGHT = 215;

/** The gap the document puts under every section, and the padding above the first one. */
export const SECTION_GAP = 16;
export const CONTENT_PADDING_TOP = 16;

/** The capture-gap note, drawn on page one when there is a gap. */
export const COVERAGE_NOTE_HEIGHT = 76;

/**
 * Where the capture's pixels come from, and the only place the two sources are weighed up.
 *
 * The render path inlines the bytes because it has no network egress; the in-app preview gets a
 * signed URL because it has no R2 credentials (POS2-1137 CP2). Exactly one is ever set, and the
 * choice lives here rather than in `capture()` because `sectionHeight` has to make the *same* one:
 * it reserves nothing for a photo section that will draw nothing, so a paginator that only knew
 * about the data URI would reserve zero height for a photo the preview then renders — and the page
 * would overflow rather than break.
 */
export function capturePhotoSrc(capture: CapturePhotoData | null): string | null {
  return capture?.image_data_uri ?? capture?.image_url ?? null;
}

export function isSectionId(value: string): value is SectionId {
  return (SECTION_IDS as readonly string[]).includes(value);
}
